/**
 * RecipientPreviewModal — Phase 3 mandatory gate before Send.
 *
 * Shows server-computed recipient breakdown:
 *   raw → duplicates removed → invalid emails removed → opt-outs removed → final
 * Plus hygiene metrics (never_contacted / not_contacted_30d / contacted_within_30d)
 * and the full per-row recipient list (email, company, contact, stage, language,
 * source, segment, last_campaign_sent, days_since_last_campaign, opt_in).
 *
 * Admin must click "Confirm Recipients" before Send is enabled. Any subsequent
 * filter mutation invalidates the confirmation on the server side (hash check)
 * so admins are forced back through this modal.
 */
import React, { useState, useMemo } from 'react';
import { X, Users, AlertCircle, CheckCircle2, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  try { return new Date(iso).toISOString().slice(0, 10); }
  catch { return iso; }
};

const StatTile = ({ label, value, tone = 'default', testid }) => {
  const toneMap = {
    default: 'border-white/10 text-white',
    good: 'border-emerald-500/40 text-emerald-400',
    warn: 'border-yellow-500/40 text-yellow-400',
    drop: 'border-red-500/40 text-red-400',
    final: 'border-[#00D4FF]/50 text-[#00D4FF] bg-[#00D4FF]/5',
  };
  return (
    <div className={`px-3 py-2 border ${toneMap[tone] || toneMap.default} bg-black/40`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-2xl font-bold mt-0.5 leading-none">{value ?? '\u2014'}</div>
    </div>
  );
};

export const RecipientPreviewModal = ({
  open,
  loading,
  preview,         // server response: { counts, hygiene, rows, filters_hash, audience_type }
  audienceType,
  onClose,
  onConfirm,       // (filters_hash) => Promise
  onRefresh,
}) => {
  const [confirming, setConfirming] = useState(false);
  const [rowsLimit, setRowsLimit] = useState(50);

  const counts = preview?.counts || {};
  const hygiene = preview?.hygiene || {};
  const rows = preview?.rows || [];

  const downloadCsv = () => {
    if (!rows.length) return;
    const headers = [
      'Email', 'Company', 'Contact', 'Stage', 'Language', 'Source',
      'Segment', 'Last Campaign Sent', 'Days Since Last Campaign', 'Opt-In',
    ];
    const csv = [headers.join(',')].concat(
      rows.map(r => [
        r.email, r.company, r.contact_name, r.stage, r.language, r.source,
        r.segment, r.last_campaign_sent, r.days_since_last_campaign,
        r.opt_in ? 'Yes' : 'No',
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `recipient_preview_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('Preview CSV downloaded');
  };

  const confirm = async () => {
    if (!preview?.filters_hash) return;
    setConfirming(true);
    try {
      await onConfirm(preview.filters_hash);
    } finally {
      setConfirming(false);
    }
  };

  const visibleRows = useMemo(() => rows.slice(0, rowsLimit), [rows, rowsLimit]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      {/* Round 1: 95vw width on 2k+ screens. max-w-6xl=72rem (~1152px)
          is the legacy cap; on 2K+ we let the modal breathe so the
          recipient table doesn't horizontally scroll. */}
      <div className="bg-[#0A0A0A] border border-[#00D4FF]/20 w-[95vw] max-w-[1700px] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="recipient-preview-modal">
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#00D4FF]" />
            <h3 className="text-lg font-bold text-white">Recipient Preview</h3>
            <span className="text-[11px] uppercase tracking-widest text-white/40">{audienceType}</span>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="recipient-preview-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-10 text-white/50">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Computing recipients...
            </div>
          )}

          {!loading && preview && (
            <>
              {/* Counts strip */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="preview-counts">
                <StatTile label="Raw Records" value={counts.raw ?? 0} testid="preview-raw" />
                <StatTile label="Duplicates Removed" value={counts.duplicates_removed ?? 0} tone={counts.duplicates_removed ? 'warn' : 'default'} testid="preview-duplicates" />
                <StatTile label="Invalid Emails Removed" value={counts.invalid_emails_removed ?? 0} tone={counts.invalid_emails_removed ? 'drop' : 'default'} testid="preview-invalid" />
                <StatTile label="Opt-Outs Removed" value={counts.unsubscribed_removed ?? 0} tone={counts.unsubscribed_removed ? 'drop' : 'default'} testid="preview-optout" />
                <StatTile label="Bounced Removed" value={counts.bounced_removed ?? 0} tone={counts.bounced_removed ? 'drop' : 'default'} testid="preview-bounced" />
                <StatTile label="Already Contacted Removed" value={counts.already_contacted_removed ?? 0} tone={counts.already_contacted_removed ? 'warn' : 'default'} testid="preview-already-contacted" />
                <StatTile label="Limit Truncated" value={counts.limit_truncated ?? 0} tone={counts.limit_truncated ? 'warn' : 'default'} testid="preview-limit-truncated" />
                <StatTile label="Final Recipients" value={counts.final ?? 0} tone="final" testid="preview-final" />
              </div>

              {/* Hygiene metrics */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Campaign Hygiene</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="preview-hygiene">
                  <StatTile label="Never Contacted" value={hygiene.never_contacted ?? 0} testid="hygiene-never" />
                  <StatTile label="Contacted within 30 days" value={hygiene.contacted_within_30d ?? 0} tone={hygiene.contacted_within_30d ? 'warn' : 'default'} testid="hygiene-recent" />
                  <StatTile label="Not Contacted in 30+ days" value={hygiene.not_contacted_30d ?? 0} testid="hygiene-old" />
                  <StatTile label="Opens (Last Camp.)" value={hygiene.opens_last_campaign ?? 'N/A'} testid="hygiene-opens" />
                  <StatTile label="Clicks (Last Camp.)" value={hygiene.clicks_last_campaign ?? 'N/A'} testid="hygiene-clicks" />
                </div>
              </div>

              {/* Empty state */}
              {rows.length === 0 && (
                <div className="border border-yellow-500/30 bg-yellow-500/5 p-4 flex items-start gap-2" data-testid="preview-empty">
                  <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <div className="text-yellow-400 font-semibold text-sm">No recipients match the current filters.</div>
                    <div className="text-white/60 text-[12px] mt-1">Adjust your customer/supplier filters and re-open the preview.</div>
                  </div>
                </div>
              )}

              {/* Table */}
              {rows.length > 0 && (
                <div className="border border-white/10" data-testid="preview-table-wrap">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                    <span className="text-[11px] text-white/60 font-mono">
                      Showing {visibleRows.length.toLocaleString()} of {rows.length.toLocaleString()} final recipients
                    </span>
                    <button onClick={downloadCsv} className="text-[11px] text-[#00D4FF] hover:underline flex items-center gap-1" data-testid="preview-download-csv">
                      <Download className="w-3 h-3" /> Download CSV
                    </button>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    <table className="w-full text-xs" data-testid="preview-rows-table">
                      <thead className="sticky top-0 bg-black/80 text-[10px] uppercase tracking-widest text-white/40">
                        <tr>
                          <th className="text-left px-3 py-2">Email</th>
                          <th className="text-left px-3 py-2">Company</th>
                          <th className="text-left px-3 py-2">Contact</th>
                          <th className="text-left px-3 py-2">Stage</th>
                          <th className="text-left px-3 py-2">Lang</th>
                          <th className="text-left px-3 py-2">Source</th>
                          <th className="text-left px-3 py-2">Segment</th>
                          <th className="text-left px-3 py-2">Last Camp.</th>
                          <th className="text-center px-3 py-2">Days</th>
                          <th className="text-center px-3 py-2">Opt-In</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map((r, i) => (
                          <tr key={(r.email || '') + i} className="border-t border-white/5" data-testid={`preview-row-${i}`}>
                            <td className="px-3 py-1.5 text-white/80 font-mono text-[11px] max-w-[220px] truncate" title={r.email}>{r.email || '\u2014'}</td>
                            <td className="px-3 py-1.5 text-white/70 max-w-[160px] truncate" title={r.company}>{r.company || '\u2014'}</td>
                            <td className="px-3 py-1.5 text-white/60 max-w-[140px] truncate" title={r.contact_name}>{r.contact_name || '\u2014'}</td>
                            <td className="px-3 py-1.5 text-white/60">{r.stage || '\u2014'}</td>
                            <td className="px-3 py-1.5 text-white/60">{r.language || '\u2014'}</td>
                            <td className="px-3 py-1.5 text-white/50 max-w-[180px] truncate" title={r.source}>{r.source || '\u2014'}</td>
                            <td className="px-3 py-1.5 text-white/50 text-[10px]">{r.segment || '\u2014'}</td>
                            <td className="px-3 py-1.5 text-white/50 text-[10px]">{fmtDate(r.last_campaign_sent)}</td>
                            <td className="px-3 py-1.5 text-center text-white/50">{r.days_since_last_campaign === '' || r.days_since_last_campaign === null ? '\u2014' : r.days_since_last_campaign}</td>
                            <td className="px-3 py-1.5 text-center">
                              {r.opt_in
                                ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline" />
                                : <X className="w-3.5 h-3.5 text-red-400 inline" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {rows.length > rowsLimit && (
                    <div className="border-t border-white/5 px-3 py-2 text-center">
                      <button onClick={() => setRowsLimit(rowsLimit + 100)} className="text-[11px] text-[#00D4FF] hover:underline" data-testid="preview-show-more">
                        Show 100 more
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-white/10 px-5 py-3 flex justify-end gap-3">
          <button onClick={onRefresh} disabled={loading} className="px-4 py-2 text-sm border border-white/20 text-white/70 hover:bg-white/5 disabled:opacity-40" data-testid="preview-refresh-btn">
            Re-run
          </button>
          <button onClick={onClose} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
          <button
            onClick={confirm}
            disabled={loading || confirming || !preview || (counts.final ?? 0) === 0}
            className="px-5 py-2 text-sm bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30 disabled:opacity-40 flex items-center gap-1.5"
            data-testid="preview-confirm-btn"
          >
            {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm {(counts.final ?? 0).toLocaleString()} Recipients
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipientPreviewModal;
